import React from "react";

import { Stack, Typography } from "@mui/material";

import { SettingsModuleProps } from "@app/analytics/account/types";
import { SwitchControlLabel } from "@app/analytics/account/WorkspaceSettings/components/styles";
import WorkspaceSettingsAccordion from "@app/analytics/account/WorkspaceSettings/components/WorkspaceSettingsAccordion";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import Switch from "@app/common/components/Switch";
import { TOOLTIPS } from "@app/common/content";

import { SettingSubtitle } from "../../components/styles";
import WorkspaceCanEditDropdown from "../components/WorkspaceCanEditDropdown";

const RecordingAndPlaybackModule = ({
  settings,
  onSettingUpdate,
  workspaceIsInactive,
}: SettingsModuleProps) => {
  const handleTranscriptionOptInEditableUpdate = (newValue: boolean) => {
    onSettingUpdate({ transcriptionOptInIsUserEditable: newValue });
  };
  const handleStoreRecordingOptInEditableUpdate = (newValue: boolean) => {
    onSettingUpdate({ storeRecordingOptInIsUserEditable: newValue });
  };

  const handleTranscriptionEnabledUpdate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onSettingUpdate({ transcriptionOptIn: event.target.checked });
  };

  const handleStoreRecordingEnabledUpdate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onSettingUpdate({ storeRecordingOptIn: event.target.checked });
  };

  return (
    <WorkspaceSettingsAccordion
      title="Recording & Playback"
      subtitle="Enable transcription, recording, and playback for this Workspace"
    >
      <Stack spacing={1.5}>
        <SettingSubtitle sx={{ fontWeight: "500" }}>
          Transcription
        </SettingSubtitle>
        <WorkspaceCanEditDropdown
          disabled={workspaceIsInactive}
          name="transcriptionOptInIsUserEditable"
          onUpdate={handleTranscriptionOptInEditableUpdate}
          selectedValue={settings.transcriptionOptInIsUserEditable}
          tooltip={
            workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
          }
        />
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <SwitchControlLabel
            disabled={workspaceIsInactive}
            control={
              <Switch
                checked={settings.transcriptionOptIn}
                onChange={handleTranscriptionEnabledUpdate}
              />
            }
            label={
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "shades.neutral.800" }}
              >
                Enable transcription for users in this Workspace
              </Typography>
            }
          />
        </UpsellTooltipWrapper>
        <SettingSubtitle sx={{ fontWeight: "500" }}>
          Audio and Video Playback
        </SettingSubtitle>
        <WorkspaceCanEditDropdown
          disabled={workspaceIsInactive}
          name="storeRecordingOptInIsUserEditable"
          onUpdate={handleStoreRecordingOptInEditableUpdate}
          selectedValue={settings.storeRecordingOptInIsUserEditable}
          tooltip={
            workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
          }
        />
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <SwitchControlLabel
            disabled={workspaceIsInactive}
            control={
              <Switch
                checked={settings.storeRecordingOptIn}
                onChange={handleStoreRecordingEnabledUpdate}
              />
            }
            label={
              <Typography
                variant="bodySecondaryReg"
                sx={{ color: "shades.neutral.800" }}
              >
                Enable audio and video playback for users in this Workspace
              </Typography>
            }
          />
        </UpsellTooltipWrapper>
        <Typography variant="labelReg" sx={{ color: "shades.neutral.600" }}>
          Enabling transcription and audio & video playback will not
          retroactively apply to past meetings. Similarly, disabling these will
          not remove access to transcripts or playback for meetings where it was
          enabled.
        </Typography>
      </Stack>
    </WorkspaceSettingsAccordion>
  );
};

export default RecordingAndPlaybackModule;
